import React, {useState, useCallback, SetStateAction, Dispatch, useEffect} from 'react';
import {View, TouchableOpacity, Platform} from 'react-native';
import {useSelector} from 'react-redux';
import {dobroruDataSelector} from '../../../../redux/selectors/dobroru';
import {ResponsiveView} from '../../../../shared/components/responsiveView';
import {Text} from '../../../../shared/components/text';
import {DeviceSize, useDeviceSize} from '../../../../shared/theme/helpers';
import {useVolunteersServiceStyles} from '../../styles';
import {Info} from './VolunteersServiceInfo';
import {Table} from './VolunteersEventsTable';
import {Link} from '../../../../shared/components/link';
import {useStyles} from '../../../../shared/hooks/styles';
import {ScoreExchange} from './scoreExchange';
import {images} from '../../../../assets/icons';
import Tooltip from 'react-native-walkthrough-tooltip';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import {apiUrls} from '../../../../shared/constants/urls';
import {getApiRoute} from '../../../../shared/pipes';
import {HttpWrapperOptions, httpService} from '../../../../shared/httpWrapper';

let timer: any = null;

const levelColorCodes: Record<string, string> = {
  'Бронзовый уровень': '#CD7F32',
  'Базовый уровень': '#142d3d',
  'Серебряный уровень': '#838996',
  'Золотой уровень': '#E8C300',
};

interface Props {
  setNestedComponentCoordinates: Dispatch<SetStateAction<{x: number; y: number}>>;
  scrollToNestedComponent: () => void;
}

export const VolunteersServiesProfile = (props: Props) => {
  const data = useSelector(dobroruDataSelector);
  const styles = useVolunteersServiceStyles();
  const [show, setShow] = useState(false);

  const deviceSize = useDeviceSize();
  const isPlatformWebAndDesktop = deviceSize === DeviceSize.desktop && Platform.OS === 'web';
  const isDesktop = useIsDesktop();

  const levelTitleColor = React.useMemo(
    () =>
      data?.level != null && levelColorCodes[data.level]
        ? levelColorCodes[data.level]
        : levelColorCodes['Базовый уровень'],
    [data?.level],
  );

  const innerStyles = useStyles(({mediaOnly}) => ({
    rootPadding: {
      paddingRight: 0,
      paddingLeft: 0,
    },
    titleRow: {
      justifyContent: isDesktop ? 'space-between' : 'flex-end',
    },
    flexRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: isDesktop ? 0 : 15,
    },
    levelTitle: {
      color: levelTitleColor,
      paddingRight: 10,
    },
    descriptionTitle: {
      marginTop: 20,
    },
    icon: {
      marginTop: isDesktop ? 8 : 0,
    },
    tooltipOverlayStyle: {
      display: 'flex',
      alignItems: 'flex-start',
      shadowRadius: 8,
      shadowOpacity: 0.3,
    },
    description: {
      ...mediaOnly(
        DeviceSize.mobile,
        {minHeight: 162, width: 360, top: Platform.OS === 'ios' ? 140 : 100, right: Platform.OS === 'ios' ? 8 : 4},
        {minHeight: 136, width: 390},
      ),
    },
  }));

  const onTextClick = useCallback(() => {
    if (isPlatformWebAndDesktop || show) {
      return;
    }
    clearTimeout(timer);
    setShow(true);
    timer = setTimeout(() => {
      setShow(false);
    }, 5000);
  }, [deviceSize, isPlatformWebAndDesktop]);

  const {HelpQuestion} = images;

  type LevelInfo = {
    level: string;
    value: (number | null)[];
  };
  const [levelsInfo, setLevelsInfo] = useState<LevelInfo[]>([]);

  useEffect(() => {
    const levelsData = async () => {
      const levels = await httpService.get<LevelInfo[]>(
        getApiRoute(apiUrls.getLevelsInfo),
        new HttpWrapperOptions().Wait(false).Errors(true).ThrowException(false),
      );
      setLevelsInfo(levels);
    };
    levelsData();
  }, []);

  return (
    <View style={[styles.root, innerStyles.rootPadding]}>
      <View style={styles.content}>
        <View style={[innerStyles.flexRow, innerStyles.titleRow]}>
          <ResponsiveView only={[DeviceSize.desktop]}>
            <Text testID='volunteers-title-id' style={styles.title} type='h2'>
              Волонтерство
            </Text>
          </ResponsiveView>
          {data?.level ? (
            <View style={innerStyles.flexRow}>
              <Text style={innerStyles.levelTitle} type={isDesktop ? 'h2' : 'h3'}>
                {data.level}
              </Text>
              <TouchableOpacity
                testID='volunteer-level-id'
                activeOpacity={1}
                onPress={onTextClick}
                style={innerStyles.icon}>
                <Tooltip
                  onClose={() => setShow(false)}
                  tooltipStyle={innerStyles.tooltipOverlayStyle}
                  backgroundColor='transparent'
                  isVisible={show}
                  disableShadow={false}
                  contentStyle={innerStyles.description}
                  content={
                    <View>
                      <Text>Уровень расчитывается за последние 365 дней</Text>
                      {levelsInfo?.map((lvl, i) => {
                        if (lvl.value[1] == null) {
                          return (
                            <Text key={i}>
                              {lvl.level}: {lvl.value[0]}+ часов
                            </Text>
                          );
                        } else {
                          return (
                            <Text key={i}>
                              {lvl.level}: {lvl.value[0]} - {lvl.value[1]} часов
                            </Text>
                          );
                        }
                      })}
                    </View>
                  }>
                  <Text numberOfLines={1} ellipsizeMode='head' onPress={() => setShow(true)} style={innerStyles.icon}>
                    <HelpQuestion height={20} width={20} />
                  </Text>
                </Tooltip>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
        <Info />
        <ScoreExchange scrollToNestedComponent={props.scrollToNestedComponent} />
        <Text testID='volunteers-description-title-id' style={[styles.descriptionTitle, innerStyles.descriptionTitle]}>
          Единая социальная карта Свердловской области позволяет волонтерам быть в курсе всех событий сферы
          добровольчества в Свердловской области.
        </Text>
        <Text testID='volunteers-description-body-id' style={styles.descriptionBody}>
          Все актуальные события, которые проводятся в регионе для добровольцев (по данным портала{' '}
          <Link external href='https://dobro.ru/' testID='volunteers-dobroru-href'>
            <Text style={styles.link}>Добро.рф</Text>
          </Link>
          ), будут отражаться в вашей новостной ленте.
        </Text>
        <Table setNestedComponentCoordinates={props.setNestedComponentCoordinates} />
      </View>
    </View>
  );
};
